import { makeAutoObservable, reaction, runInAction } from 'mobx';
import debounce from "lodash/debounce";

import { StockistBasketItemDTO } from "../DTOs/Basket/StockistBasketItemDTO";
import { basketController } from "./BasketController";
import { alertController } from "./AlertController";
import { issueController } from "./IssueController";
import { userAccountController } from "./UserAccountController";
import { filteringController } from "../../Pages/MainPage/Controllers/FilteringController";
import { countryController } from './CountryController';
import { addressesController } from './AddressesController';
import { orderController } from "../../Pages/CheckoutPage/Controllers/OrderController";
import { StockistIssue } from '../DTOs/StockistIssue';

export class StockiestPortalController {
  orderedProducts: StockistBasketItemDTO[] = [];
  basketDrawerOpen = false;
  isAwaitingServerSync = false;
  isIssuesForExtrasLoading = false;
  showIssueNavigation = false;
  private _debouncedPersistOrderedProduct: (item: StockistBasketItemDTO, quantity: number) => void;

  get issues() {
    return issueController.getIssues()
  }

  get purchasableOrUpcomingOrderedProductsPresent() {
    return (this.purchasableOrderedProducts && this.purchasableOrderedProducts.length > 0)
      || (this.upcomingOrderedProducts && this.upcomingOrderedProducts.length > 0)
  }

  get purchasableOrderedProducts(): StockistBasketItemDTO[] {
    return this.usingLocalData
      ? this.orderedProducts.filter((op) => !op.validation?.isUpcoming && op.issue.stock.stockAvailableForStockist > 0)
      : basketController.basket?.purchasableItems ?? []
  }

  get upcomingOrderedProducts(): StockistBasketItemDTO[] {
    return this.usingLocalData
      ? this.orderedProducts.filter((op) => op.validation?.isUpcoming)
      : basketController.basket?.items.filter(item => item.validation?.isUpcoming) ?? []
  }

  get issuesForExtras() {
    return userAccountController.issuesForExtras;
  }

  get basket() {
    return basketController.basket
  }

  get allIssuesPulled() {
    return issueController.getAllIssuesPulled()
  }

  get loadingIssues() {
    return issueController.loadingIssues
  }

  get isBasketDataLoading() {
    return basketController.isBasketDataLoading
  }

  async searchIssues() {
    await issueController.searchForIssues()
    if (this.issues.length === 0) {
      await this.pullMoreIssues()
      return
    }
  }

  private get usingLocalData() {
    return this.isAwaitingServerSync || !basketController.basket;
  }

  private get totalIssuePriceLocal() {
    return this.purchasableOrderedProducts.reduce(
      (sum, p) => sum + (p.totalIssuePrice ?? p.issuePrice * p.quantity),
      0
    );
  }

  private get totalDiscountAmountLocal() {
    return this.purchasableOrderedProducts.reduce(
      (sum, p) => sum + ((p.totalIssuePrice - p.totalDiscountedIssuePrice) ?? 0),
      0
    );
  }

  private get totalDiscountedIssuePriceLocal() {
    return this.totalIssuePriceLocal - this.totalDiscountAmountLocal;
  }

  get totalIssuePrice() {
    return this.usingLocalData
      ? this.totalIssuePriceLocal
      : basketController.basket?.totalIssuePrice;
  }

  get totalDiscountAmount() {
    return this.usingLocalData
      ? this.totalDiscountAmountLocal
      : basketController.basket?.totalDiscountAmount;
  }

  get totalDiscountedIssuePrice() {
    return this.usingLocalData
      ? this.totalDiscountedIssuePriceLocal
      : basketController.basket?.totalDiscountedIssuePrice;
  }

  get deliveryFrom() {
    return basketController.basket?.deliveryFrom?.price ?? 0;
  }

  get deliveryAddress(): { postCode: string; country: string } | undefined {
    const basket = this.basket;
    if (!basket || !basket.addressId) return undefined;

    const addr = addressesController.addresses.find(a => a.id === basket.addressId);
    if (!addr) return undefined;
    const countryObj = countryController.countries.find(c => c.iso_a2 === addr.country);
    const countryName = countryObj ? countryObj.name : addr.country;

    return {
      postCode: addr.postCode,
      country: countryName
    };
  }

  get totalPriceInclDiscountInPounds() {
    return this.totalDiscountedIssuePrice ?? 0;
  }

  get totalPriceInclDiscountInPoundsWithDelivery() {
    return +this.totalPriceInclDiscountInPounds.toFixed(2) + (+(orderController.selectedQuote?.costToPublisher.toFixed(2) ?? 0));
  }

  toggleBasketDrawerOpen = (open: boolean) => {
    runInAction(() => this.basketDrawerOpen = open);
  }

  addOrderedProduct = async (issue: StockistIssue, quantity: number) => {
    const duplicateItem = this.orderedProducts.find(op => op.issue.id === issue.id)
      ?? basketController.basket?.items.find(i => i.issueId === issue.id);

    try {
      if (duplicateItem) {
        this.updateOrderedProduct(duplicateItem, quantity);
      } else {
        await basketController.addToBasket(issue.id, quantity);
      }
    } catch (error) {
      alertController.displayAlert("Error while adding product to the basket", (error as Error).message, "error")
    }
  }

  updateOrderedProduct = (item: StockistBasketItemDTO, quantity: number) => {
    runInAction(() => this.isAwaitingServerSync = true);
    item.changeOrderedAmount(quantity);
    const idx = this.orderedProducts.findIndex(op => op.issue.id === item.issue.id);
    if (idx >= 0) {
      this.orderedProducts[idx] = item;
    }
    this._debouncedPersistOrderedProduct(item, quantity);
  };

  removeOrderedProduct = async (item: StockistBasketItemDTO) => {
    try {
      await basketController.removeFromBasket(item)
      item.issue.updateInBasket(0)
      runInAction(() => {
        this.orderedProducts = this.orderedProducts.filter(op => op !== item);
      });
    } catch (error) {
      alertController.displayAlert("Error while removing product from the basket", (error as Error).message, "error")
    }
  }

  async pullMoreIssues() {
    if (filteringController.currentTab !== "latest") return;
    await issueController.pullMoreIssues();
  }

  async pullIssuesForExtras() {
    this.isIssuesForExtrasLoading = true;
    await userAccountController.getIssuesForExtras();
    runInAction(() => this.isIssuesForExtrasLoading = false)
  }

  constructor() {
    makeAutoObservable(this);

    this._debouncedPersistOrderedProduct = debounce(
      async (item: StockistBasketItemDTO, quantity: number) => {
        try {
          item.setUpdatingQuantity(true);
          await basketController.updateProduct(item, quantity);
        } catch (error) {
          alertController.displayAlert("Error while updating basket", (error as Error).message, "error");
        } finally {
          item.setUpdatingQuantity(false);
        }
      }, 1000);

    (async () => {
      await userAccountController.initializeUser()
    })();

    this.pullMoreIssues = debounce(
      this.pullMoreIssues.bind(this),
      300
    ) as () => Promise<void>;

    reaction(
      () => basketController.basket,
      () => {
        this.isAwaitingServerSync = false;
      }
    );
  }
}

export const stockiestPortalController = new StockiestPortalController();

